import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  DownloadOutlined as DownloadIcon,
  DeleteOutlined as DeleteIcon,
  SwapHorizOutlined as ReplaceIcon,
  DescriptionOutlined as DocumentIcon,
} from '@mui/icons-material';
import { getSecureDocumentUrl } from '../../utils/secureFileAccess';

const DocumentViewerModal = ({ open, onClose, document, onDelete, onReplace, onDownload }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [securePreviewUrl, setSecurePreviewUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const rawUrl = document?.fileUrl || document?.path || document?.document_path;
    if (!open || !rawUrl) {
      setSecurePreviewUrl(null);
      return undefined;
    }
    getSecureDocumentUrl(rawUrl)
      .then((url) => {
        if (!cancelled) setSecurePreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setSecurePreviewUrl(rawUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [open, document]);

  if (!document) return null;

  const documentType = document.documentType || document.typeCode || document.type;
  const documentTypeLabel = document.documentTypeLabel || document.typeLabel || documentType;
  const documentNumber = document.documentNumber ?? document.number ?? '';
  
  const previewUrl = securePreviewUrl;
  
  const previewMime = document.fileType || document.mimeType || document.mime_type || (
    String(previewUrl || '').toLowerCase().includes('.pdf') ? 'application/pdf' : null
  );

  const handleClose = () => {
    setDeleteConfirm(false);
    onClose();
  };

  const handleDelete = async () => {
    if (typeof onDelete !== 'function') {
      console.error('DocumentViewerModal: onDelete handler is not configured');
      return;
    }
    if (deleteConfirm) {
      try {
        await Promise.resolve(onDelete(document.documentId ?? document.id));
        handleClose();
      } catch (_) {
        setDeleteConfirm(false);
      }
    } else {
      setDeleteConfirm(true);
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(document);
    }
  };

  const handleReplace = () => {
    if (onReplace) {
      onReplace(document);
    }
  };

  const getExpiryStatus = () => {
    if (!document.expiryDate) return null;

    const today = new Date();
    const expiry = new Date(document.expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return {
        label: 'Expired',
        color: 'error',
        message: `Expired ${Math.abs(daysUntilExpiry)} days ago`,
      };
    } else if (daysUntilExpiry <= 30) {
      return {
        label: 'Expiring Soon',
        color: 'error',
        message: `${daysUntilExpiry} days remaining`,
      };
    } else if (daysUntilExpiry <= 60) {
      return {
        label: 'Warning',
        color: 'warning',
        message: `${daysUntilExpiry} days remaining`,
      };
    } else {
      return {
        label: 'Valid',
        color: 'success',
        message: `${daysUntilExpiry} days remaining`,
      };
    }
  };

  const expiryStatus = getExpiryStatus();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      'emirates_id': 'Emirates ID',
      'passport': 'Passport',
      'visa': 'Visa',
      'work_permit': 'Work Permit',
      'trade_license': 'Trade License',
      'board_resolution': 'Board Resolution',
      'power_of_attorney': 'Power of Attorney',
      'other': 'Other',
    };
    return types[type] || type;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon />
            <Typography variant="h6" component="div">
              {documentTypeLabel || getDocumentTypeLabel(documentType)}
              {documentNumber ? ` - ${documentNumber}` : ''}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {deleteConfirm && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Are you sure you want to delete this document? This action cannot be undone.
          </Alert>
        )}

        {/* Document Preview */}
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            height: 400,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {previewUrl ? (
            previewMime === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                title="Document Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            ) : (
              <img
                src={previewUrl}
                alt="Document"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            )
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Preview not available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click download to view the document
              </Typography>
            </Box>
          )}
        </Box>

        {/* Document Details */}
        <Typography variant="h6" gutterBottom>
          Document Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Document Type & Number */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Document Type
              </Typography>
              <Typography variant="body1">
                {getDocumentTypeLabel(documentTypeLabel || documentType)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Document Number
              </Typography>
              <Typography variant="body1">
                {documentNumber || 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Issue Date & Expiry Date */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Issue Date
              </Typography>
              <Typography variant="body1">
                {formatDate(document.issueDate)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Expiry Date
              </Typography>
              <Typography variant="body1">
                {formatDate(document.expiryDate)}
              </Typography>
            </Box>
          </Box>

          {/* Status */}
          {expiryStatus && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={expiryStatus.label}
                  color={expiryStatus.color}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  ({expiryStatus.message})
                </Typography>
              </Box>
            </Box>
          )}

          {/* File Info */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                File Name
              </Typography>
              <Typography variant="body1" noWrap>
                {document.fileName || 'document.pdf'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                File Size
              </Typography>
              <Typography variant="body1">
                {formatFileSize(document.fileSize)}
              </Typography>
            </Box>
          </Box>

          {/* Upload Info */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Uploaded By
              </Typography>
              <Typography variant="body1">
                {document.uploadedBy || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Upload Date
              </Typography>
              <Typography variant="body1">
                {formatDate(document.uploadedDate)}
              </Typography>
            </Box>
          </Box>

          {/* Notes */}
          {document.notes && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {document.notes}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          color="primary"
        >
          Download
        </Button>
        {onReplace && (
          <Button
            startIcon={<ReplaceIcon />}
            onClick={handleReplace}
            color="primary"
          >
            Replace
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {typeof onDelete === 'function' && (
          <Button
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            color={deleteConfirm ? 'error' : 'inherit'}
            variant={deleteConfirm ? 'contained' : 'text'}
          >
            {deleteConfirm ? 'Confirm Delete' : 'Delete'}
          </Button>
        )}
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewerModal;
